import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Palette, Mail, Building, Shield, Bell, Database, CheckCircle, Send, RotateCcw } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { getSettings, updateSetting } from '../api/settings';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const SettingsPageEnhanced = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeTab, setActiveTab] = useState('branding');
  const [testingEmail, setTestingEmail] = useState(false);
  const [notifications, setNotifications] = useState({ email: true, sms: false, push: false });
  const isAdmin = user?.role === 'admin';

  const defaultSettings = {
    pdf_branding: { primaryColor: '#5A4DFF', logoUrl: '', headerText: 'CogniQ Assessment Report' },
    email_templates: { report_subject: 'Your CogniQ Assessment Report', report_body: 'Your assessment report is ready...' },
    org_info: { companyName: 'CogniQ', supportEmail: 'support@cogniq.com', website: 'https://cogniq.com' }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await getSettings();
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to load settings:', error);
        if (error.response?.status !== 401) {
          toast.error('Failed to load settings');
        }
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSave = async (key, value) => {
    if (!isAdmin) return;
    
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await updateSetting(key, value);
      setSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleInputChange = (key, field, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value
      }
    }));
  };

  const handleTestEmail = async () => {
    if (!isAdmin) return;
    setTestingEmail(true);
    try {
      await apiClient.post('/settings/test-email', {}, {
        headers: { Authorization: `Bearer ${await user.getIdToken()}` }
      });
      toast.success('Test email sent successfully');
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleResetToDefaults = () => {
    if (!isAdmin) return;
    if (!confirm('Reset all settings to defaults?')) return;
    setSettings(defaultSettings);
    toast.success('Settings reset to defaults');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'organization', label: 'Organization', icon: Building },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  return (
    <>
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">
          {isAdmin ? 'Configure system settings and preferences.' : 'View system settings (read-only).'}
        </p>
      </motion.div>

      {/* Tabs */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="space-y-8">
        {/* PDF Branding Settings */}
        {activeTab === 'branding' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <Palette className="w-5 h-5 text-purple-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">PDF Branding</h3>
                  </div>
                  <span className="text-sm text-gray-500">Customize PDF report appearance</span>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={settings.pdf_branding?.primaryColor || '#5A4DFF'}
                        onChange={(e) => handleInputChange('pdf_branding', 'primaryColor', e.target.value)}
                        disabled={!isAdmin}
                        className="w-16 h-12 border-2 border-gray-300 rounded-lg cursor-pointer disabled:cursor-not-allowed"
                      />
                      <input
                        type="text"
                        value={settings.pdf_branding?.primaryColor || '#5A4DFF'}
                        onChange={(e) => handleInputChange('pdf_branding', 'primaryColor', e.target.value)}
                        disabled={!isAdmin}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                      />
                      <div 
                        className="w-12 h-12 rounded-lg border-2 border-gray-300"
                        style={{ backgroundColor: settings.pdf_branding?.primaryColor || '#5A4DFF' }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Used for headers and accents in PDF reports</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
                    <input
                      type="url"
                      value={settings.pdf_branding?.logoUrl || ''}
                      onChange={(e) => handleInputChange('pdf_branding', 'logoUrl', e.target.value)}
                      disabled={!isAdmin}
                      placeholder="https://example.com/logo.png"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    />
                    <p className="mt-1 text-xs text-gray-500">Company logo displayed on PDF reports</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Header Text</label>
                    <input
                      type="text"
                      value={settings.pdf_branding?.headerText || ''}
                      onChange={(e) => handleInputChange('pdf_branding', 'headerText', e.target.value)}
                      disabled={!isAdmin}
                      placeholder="CogniQ Assessment Report"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    />
                    <p className="mt-1 text-xs text-gray-500">Main heading text for PDF reports</p>
                  </div>
                </div>
                
                {isAdmin && (
                  <div className="mt-8 flex items-center justify-between pt-6 border-t">
                    <p className="text-sm text-gray-600">Changes will apply to all new PDF reports</p>
                    <Button
                      onClick={() => handleSave('pdf_branding', settings.pdf_branding)}
                      loading={saving.pdf_branding}
                      className="flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Email Templates */}
        {activeTab === 'email' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Email Templates</h3>
                  </div>
                  <span className="text-sm text-gray-500">Customize email notifications</span>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Report Email Subject</label>
                    <input
                      type="text"
                      value={settings.email_templates?.report_subject || 'Your CogniQ Assessment Report'}
                      onChange={(e) => handleInputChange('email_templates', 'report_subject', e.target.value)}
                      disabled={!isAdmin}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    />
                    <p className="mt-1 text-xs text-gray-500">Subject line for report delivery emails</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Report Email Body</label>
                    <textarea
                      rows={6}
                      value={settings.email_templates?.report_body || 'Your assessment report is ready...'}
                      onChange={(e) => handleInputChange('email_templates', 'report_body', e.target.value)}
                      disabled={!isAdmin}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 font-mono text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">Email body template (supports HTML)</p>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Available Variables</h4>
                    <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
                      <div><code className="bg-blue-100 px-2 py-1 rounded">{'{{name}}'}</code> - User name</div>
                      <div><code className="bg-blue-100 px-2 py-1 rounded">{'{{email}}'}</code> - User email</div>
                      <div><code className="bg-blue-100 px-2 py-1 rounded">{'{{date}}'}</code> - Report date</div>
                      <div><code className="bg-blue-100 px-2 py-1 rounded">{'{{link}}'}</code> - Download link</div>
                    </div>
                  </div>
                </div>
                
                {isAdmin && (
                  <div className="mt-8 flex items-center justify-between pt-6 border-t">
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="outline"
                        onClick={handleTestEmail}
                        loading={testingEmail}
                        className="flex items-center space-x-2"
                      >
                        <Send className="w-4 h-4" />
                        <span>Test SMTP</span>
                      </Button>
                      <p className="text-sm text-gray-600">Send test email to verify configuration</p>
                    </div>
                    <Button
                      onClick={() => handleSave('email_templates', settings.email_templates)}
                      loading={saving.email_templates}
                      className="flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Organization Info */}
        {activeTab === 'organization' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <Building className="w-5 h-5 text-green-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Organization Details</h3>
                  </div>
                  <span className="text-sm text-gray-500">Company information</span>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      value={settings.org_info?.companyName || 'CogniQ'}
                      onChange={(e) => handleInputChange('org_info', 'companyName', e.target.value)}
                      disabled={!isAdmin}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Support Email</label>
                    <input
                      type="email"
                      value={settings.org_info?.supportEmail || 'support@cogniq.com'}
                      onChange={(e) => handleInputChange('org_info', 'supportEmail', e.target.value)}
                      disabled={!isAdmin}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <input
                      type="url"
                      value={settings.org_info?.website || 'https://cogniq.com'}
                      onChange={(e) => handleInputChange('org_info', 'website', e.target.value)}
                      disabled={!isAdmin}
                      placeholder="https://example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                </div>
                
                {isAdmin && (
                  <div className="mt-8 flex items-center justify-between pt-6 border-t">
                    <p className="text-sm text-gray-600">Organization details used in reports and emails</p>
                    <Button
                      onClick={() => handleSave('org_info', settings.org_info)}
                      loading={saving.org_info}
                      className="flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Security Settings */}
        {activeTab === 'security' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <Shield className="w-5 h-5 text-red-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Security & Compliance</h3>
                  </div>
                  <span className="text-sm text-gray-500">Security configurations</span>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">PDF Encryption</p>
                        <p className="text-sm text-gray-600">AES-256 encryption enabled</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-green-600">Active</span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Firebase Authentication</p>
                        <p className="text-sm text-gray-600">JWT token validation</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-green-600">Active</span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">GDPR Compliance</p>
                        <p className="text-sm text-gray-600">Data privacy regulations</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-green-600">Compliant</span>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                      <div>
                        <p className="font-medium text-gray-900">Rate Limiting</p>
                        <p className="text-sm text-gray-600">300 requests per 15 minutes</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-green-600">Active</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Notification Preferences */}
        {activeTab === 'notifications' && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <Bell className="w-5 h-5 text-indigo-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">Notification Preferences</h3>
                  </div>
                  <span className="text-sm text-gray-500">Configure alert channels</span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">Email Notifications</p>
                      <p className="text-sm text-gray-600">Receive alerts via email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifications.email}
                        onChange={(e) => setNotifications(prev => ({ ...prev, email: e.target.checked }))}
                        disabled={!isAdmin}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg opacity-50">
                    <div>
                      <p className="font-medium text-gray-900">SMS Notifications</p>
                      <p className="text-sm text-gray-600">Receive alerts via SMS (Coming soon)</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-not-allowed">
                      <input type="checkbox" disabled className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg opacity-50">
                    <div>
                      <p className="font-medium text-gray-900">Push Notifications</p>
                      <p className="text-sm text-gray-600">Browser push notifications (Coming soon)</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-not-allowed">
                      <input type="checkbox" disabled className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full"></div>
                    </label>
                  </div>
                </div>
                
                {isAdmin && (
                  <div className="mt-8 flex items-center justify-between pt-6 border-t">
                    <p className="text-sm text-gray-600">Notification preferences for system alerts</p>
                    <Button
                      onClick={() => handleSave('notifications', notifications)}
                      loading={saving.notifications}
                      className="flex items-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save Changes</span>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* Reset to Defaults Button */}
      {isAdmin && (
        <motion.div
          className="mt-6 flex justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            variant="outline"
            onClick={handleResetToDefaults}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Reset to Defaults</span>
          </Button>
        </motion.div>
      )}

      {!isAdmin && (
        <motion.div
          className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-sm text-yellow-800 flex items-center">
            <Bell className="w-4 h-4 mr-2" />
            You have read-only access to settings. Contact an administrator to make changes.
          </p>
        </motion.div>
      )}
    </>
  );
};

export default SettingsPageEnhanced;
